/**
 * Ficha da frente de obra (#365, D11): o cabeçalho com o nome e a situação, o cartão "Situação
 * da frente" e três abas — Etapas, Riscos e Trabalhadores — cada uma com a contagem. Encerrar e
 * reabrir moram aqui; a exclusão também, e com trabalhador vinculado ela é recusada e oferece
 * "Encerrar frente" (D7). Encerrar fecha os vínculos abertos com a data de hoje (D19).
 *
 * Alvo visual: docs/superpowers/specs/2026-10-16-365-frentes-obra-mockup.html (tela 3).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Skeleton, Tab, Tabs } from '@mui/material';
import { Construction as ConstructionIcon } from '@mui/icons-material';

import { IdentityBanner, PageShell, RouteActionButton } from '../../components';
import { ActionButton } from '../../components/common/ActionButton';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { DeleteWithDependenciesDialog } from '../../components/common/DeleteWithDependenciesDialog';
import { pode } from '../../authorization/pode';
import { useDadoPermitido } from '../../authorization/useListaPermitida';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';
import { unitService } from '../../services/unitService';
import { workFrontService } from '../../services/workFrontService';
import { ApiRequestError } from '../../types/common';
import type {
  WorkFrontDependencies,
  WorkFrontDetail,
  WorkFrontEligibleEmployee,
  WorkFrontRisksResponse,
  WorkFrontWorkerItem,
} from '../../types/workFront';
import FrenteSituacaoChip from './components/FrenteSituacaoChip';
import FrontSituationCard from './components/FrontSituationCard';
import RisksTab from './components/RisksTab';
import StagesTab, { FRONT_CLOSED_REASON } from './components/StagesTab';
import WorkersTab from './components/WorkersTab';

const TOUCH_TARGET = 44;

const errorMessage = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

const isStatus = (err: unknown, status: number): boolean => err instanceof ApiRequestError && err.status === status;

interface FichaData {
  front: WorkFrontDetail;
  risks: WorkFrontRisksResponse;
  workers: WorkFrontWorkerItem[];
  eligible: WorkFrontEligibleEmployee[];
}

interface TabPanelProps {
  index: number;
  value: number;
  children: React.ReactNode;
}

/** O painel fica montado quando a aba não está aberta: a seleção de riscos não salva não se perde. */
const TabPanel: React.FC<TabPanelProps> = ({ index, value, children }) => (
  <Box role="tabpanel" id={`frente-painel-${index}`} aria-labelledby={`frente-aba-${index}`} hidden={value !== index} sx={{ pt: 3 }}>
    {children}
  </Box>
);

const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);

/** O que a exclusão leva embora, em linhas curtas para o bloco de bloqueio. */
function dependencyLines(dependencies: WorkFrontDependencies | null): string[] {
  if (!dependencies) return [];
  const { stagesCount, markedRisksCount, workersCount } = dependencies;
  return [
    `${stagesCount} ${plural(stagesCount, 'etapa', 'etapas')}`,
    `${markedRisksCount} ${plural(markedRisksCount, 'risco marcado', 'riscos marcados')}`,
    `${workersCount} ${plural(workersCount, 'trabalhador vinculado', 'trabalhadores vinculados')}`,
  ];
}

export const WorkFrontDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState<FichaData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [tab, setTab] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  // A ficha que está na URL agora: a resposta de outra, que chega atrasada, é descartada.
  const currentId = useRef(id);
  useEffect(() => {
    currentId.current = id;
  }, [id]);

  const load = useCallback(async () => {
    if (!id) return;
    const front = await workFrontService.getById(id);
    const [risks, workers, eligible] = await Promise.all([
      workFrontService.getRisks(id),
      workFrontService.listWorkers(id),
      workFrontService.eligibleEmployees(front.unit.id),
    ]);
    if (currentId.current !== id) return;
    setData({ front, risks, workers, eligible });
  }, [id]);

  useEffect(() => {
    let alive = true;
    setError(null);
    setNotFound(false);
    setForbidden(false);
    setData(null);
    load().catch((err: unknown) => {
      if (!alive) return;
      if (isStatus(err, 404)) setNotFound(true);
      else if (isStatus(err, 403)) setForbidden(true);
      else setError(errorMessage(err, 'Erro ao carregar a frente'));
    });
    return () => {
      alive = false;
    };
  }, [load, attempt]);

  /** Recarrega depois de uma ação; a falha entra no aviso da tela, não derruba a ficha. */
  const reload = useCallback(async () => {
    try {
      await load();
    } catch (err: unknown) {
      if (currentId.current !== id) return;
      setActionError(errorMessage(err, 'Erro ao atualizar a ficha'));
    }
  }, [load, id]);

  const unitId = data?.front.unit.id;
  const loadAlcance = useCallback(() => unitService.getAlcance(unitId ?? ''), [unitId]);
  const alcance = useDadoPermitido('unit.read', 'Setores da obra', loadAlcance, { habilitado: unitId !== undefined });

  const closeFront = async () => {
    if (!id) return;
    setBusy(true);
    setCloseError(null);
    try {
      await workFrontService.close(id);
      setConfirmingClose(false);
      enqueueSnackbar('Frente encerrada.', { variant: 'success' });
      await reload();
    } catch (err: unknown) {
      setCloseError(errorMessage(err, 'Não foi possível encerrar a frente'));
    } finally {
      setBusy(false);
    }
  };

  const reopenFront = async () => {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    try {
      await workFrontService.reopen(id);
      enqueueSnackbar('Frente reaberta.', { variant: 'success' });
      await reload();
    } catch (err: unknown) {
      setActionError(errorMessage(err, 'Não foi possível reabrir a frente'));
    } finally {
      setBusy(false);
    }
  };

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<WorkFrontDetail, WorkFrontDependencies>({
      checkDependencies: (frontId) => workFrontService.dependencies(frontId),
      deleteEntity: (frontId) => workFrontService.remove(frontId),
      checkErrorMessage: 'Erro ao verificar o que a frente tem',
      deleteErrorMessage: 'Erro ao excluir a frente',
      onError: setActionError,
      onDeleted: () => {
        enqueueSnackbar('Frente excluída.', { variant: 'success' });
        navigate('/work-fronts');
      },
    });

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Frentes de obra', path: '/work-fronts' },
    { label: data?.front.name ?? 'Ficha' },
  ];
  const backButton = (
    <RouteActionButton
      para="/work-fronts"
      exige={pode('work_front.read')}
      variant="outlined"
      size="small"
      sx={{ minHeight: TOUCH_TARGET }}
    >
      Voltar
    </RouteActionButton>
  );

  if (notFound) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={backButton}>
        <Alert severity="info">Frente não encontrada. Ela pode ter sido excluída ou ser de outra empresa.</Alert>
      </PageShell>
    );
  }

  if (forbidden) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={backButton}>
        <Alert severity="info">Seu perfil não inclui o acesso a esta frente. Peça a quem administra a empresa.</Alert>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={backButton}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => setAttempt((count) => count + 1)} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          {error}
        </Alert>
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell breadcrumbs={breadcrumbs} actions={backButton}>
        <Skeleton variant="rounded" height={96} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={140} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={260} />
      </PageShell>
    );
  }

  const { front, risks, workers, eligible } = data;
  const closed = front.status === 'CLOSED';
  const openWorkers = workers.filter((worker) => !worker.exitDate).length;
  const blockedByWorkers = (deleteDependencies?.workersCount ?? 0) > 0;
  const deletionLeaves = dependencyLines(deleteDependencies);

  const headerActions = (
    <>
      {backButton}
      <RouteActionButton
        para={`/work-fronts/${front.id}/edit`}
        exige={pode('work_front.update')}
        variant="outlined"
        size="small"
        sx={{ minHeight: TOUCH_TARGET }}
      >
        Editar dados
      </RouteActionButton>
    </>
  );

  return (
    <PageShell breadcrumbs={breadcrumbs} actions={headerActions}>
      <IdentityBanner
        avatar={<ConstructionIcon />}
        name={front.name}
        status={<FrenteSituacaoChip situation={front.situation} lateDays={front.lateDays} />}
        context={`${front.unit.name} › ${front.responsible?.name ?? '—'}`}
      />

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {closed && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {FRONT_CLOSED_REASON}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <ActionButton
          acao="work_front.update"
          rotulo={closed ? 'Reabrir frente' : 'Encerrar frente'}
          variant="text"
          color="inherit"
          disabled={busy}
          sx={{ minHeight: TOUCH_TARGET }}
          onClick={() => {
            if (closed) void reopenFront();
            else {
              setCloseError(null);
              setConfirmingClose(true);
            }
          }}
        >
          {closed ? 'Reabrir frente' : 'Encerrar frente'}
        </ActionButton>
        <ActionButton
          acao="work_front.delete"
          rotulo="Excluir frente"
          variant="text"
          color="error"
          disabled={busy}
          sx={{ minHeight: TOUCH_TARGET }}
          onClick={() => void handleDeleteClick(front)}
        >
          Excluir frente
        </ActionButton>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FrontSituationCard front={front} />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_event, value: number) => setTab(value)} aria-label="Etapas, riscos e trabalhadores da frente">
          <Tab id="frente-aba-0" aria-controls="frente-painel-0" label={`Etapas (${front.stagesCount})`} sx={{ minHeight: TOUCH_TARGET }} />
          <Tab id="frente-aba-1" aria-controls="frente-painel-1" label={`Riscos (${front.markedRisksCount})`} sx={{ minHeight: TOUCH_TARGET }} />
          <Tab
            id="frente-aba-2"
            aria-controls="frente-painel-2"
            label={`Trabalhadores (${front.activeWorkersCount})`}
            sx={{ minHeight: TOUCH_TARGET }}
          />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <StagesTab front={front} closed={closed} onChanged={reload} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <RisksTab
          front={front}
          risks={risks}
          closed={closed}
          unitWithoutDepartments={alcance.dado?.setores === 0}
          onChanged={reload}
        />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <WorkersTab front={front} workers={workers} eligible={eligible} closed={closed} onChanged={reload} />
      </TabPanel>

      <ConfirmDialog
        open={confirmingClose}
        title="Encerrar frente"
        message={
          openWorkers === 0
            ? `Encerrar a frente ${front.name}? Nenhum trabalhador está vinculado agora. Você pode reabri-la depois.`
            : `Encerrar a frente ${front.name}? ${
                openWorkers === 1
                  ? 'O trabalhador ainda vinculado terá a saída registrada hoje.'
                  : `Os ${openWorkers} trabalhadores ainda vinculados terão a saída registrada hoje.`
              } Reabrir a frente depois não reabre esses vínculos.`
        }
        confirmLabel="Encerrar frente"
        loading={busy}
        error={closeError}
        onConfirm={() => void closeFront()}
        onCancel={() => setConfirmingClose(false)}
      />

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={blockedByWorkers}
        title="Excluir frente"
        confirmText={`Tem certeza que deseja excluir esta frente? A exclusão é definitiva e leva junto ${deletionLeaves.slice(0, 2).join(' e ')}.`}
        prerequisite={{
          title: 'Esta frente tem trabalhadores vinculados',
          reason:
            'O histórico de quem trabalhou em cada frente é registro de fiscalização e não pode ser apagado junto com ela. Para tirá-la de circulação sem perder o histórico, encerre a frente.',
          items: deletionLeaves,
          resolve: closed
            ? undefined
            : {
                label: 'Encerrar frente',
                permission: 'work_front.update',
                mode: 'modal',
                onResolve: () => {
                  closeDeleteDialog();
                  setCloseError(null);
                  setConfirmingClose(true);
                },
              },
          deniedHint: closed
            ? 'Esta frente já está encerrada e o histórico dela fica guardado.'
            : 'Peça a um administrador para encerrar esta frente.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default WorkFrontDetailPage;
